import { RefData } from '@rialtic/utils'
import type { TimePeriod } from '../types'
import { applyBufferDayAdjustment } from './bufferDays'
import { isIcd10InDefinedList } from './icd10DefinedList'
import {
  POLICY_INDICATION_EDIT_TYPE,
  formatDrugPolicyName,
  parseIndicationTypes,
  type PolicyIndicationEditType,
} from './editTypes'

/**
 * Narrow drug shape consumed by the DDE export. Declares exactly the fields the
 * builders read so both the worker `Drug` state type and the admin derived
 * `Drug` type satisfy it without depending on either.
 */
export interface DdeDrugInput {
  rialticDrugName?: string
  genericName: string
  brandName: string
  description: string
  formulation: string
}

/**
 * Narrow indication shape consumed by the DDE export. Declares exactly the
 * fields the builders read, with the optionality the current code tolerates.
 */
export interface DdeIndicationInput {
  name?: string
  type?: string
  hcpcsCodes?: string[]
  icd10Codes?: {
    code: string
    active?: boolean
    isRemoved?: boolean
    acceptanceState?: 'included' | 'excluded'
  }[]
  ageMin?: number | string | null
  ageMinUom?: string | null
  ageMax?: number | string | null
  ageMaxUom?: string | null
  frequencyMaxAdminDays?: number
  frequencyMaxFrequency?: number
  frequencyTimePeriod?: TimePeriod | null
  maxDailyHcpcsUnits?: number
  maxUnitsOverTimeMaxHCPCSUnits?: number
  maxUnitsOverHcpcsMaxFrequency?: number
  maxUnitsOverHcpcsTimePeriod?: TimePeriod | null
}

export interface DDEDrugDetails {
  eff_start_date: number
  eff_end_date: number
  drugname: string
  brandname: string
  drugdescription: string
  modeofadministration: string
}
export const buildDrugDetails = (drug: DdeDrugInput): DDEDrugDetails => {
  return {
    // oxlint-disable-next-line unicorn/numeric-separators-style
    eff_start_date: 2024_07_01,
    // oxlint-disable-next-line unicorn/numeric-separators-style
    eff_end_date: 9999_12_31,
    drugname: drug.genericName,
    brandname: drug.brandName,
    drugdescription: drug.description,
    modeofadministration: drug.formulation,
  }
}

export interface DDEIndicationDetails {
  drugcode: string
  indication: string
  dxcodes: string
  associatedndcs: string
  reviewer_notes: string
  off_label: 'On-Label' | 'Off-Label'
  defense: string
}

export const buildIndicationDetails = (
  indication: DdeIndicationInput,
): DDEIndicationDetails => {
  return {
    drugcode: indication.hcpcsCodes?.join('|') ?? '',
    indication: indication.name ?? '',
    dxcodes: formatDxCodes(indication.icd10Codes ?? []),
    associatedndcs: '',
    reviewer_notes: '',
    off_label: indication.type === 'ON_LABEL' ? 'On-Label' : 'Off-Label',
    defense: '',
  }
}

/**
 * Takes an array of diagnosis codes and returns a string of the codes separated by a pipe
 * G35|G35.A|G35.B*|G35.C*|G35.D|G37.8*
 * @param codes
 * @returns string
 */
export const formatDxCodes = (
  codes: {
    code: string
    active?: boolean
    isRemoved?: boolean
    acceptanceState?: 'included' | 'excluded'
  }[],
) => {
  return (
    codes
      ?.filter((c) => {
        if (!c?.code?.trim()) return false
        return isIcd10InDefinedList(c)
      })
      .map((code) => RefData.formatIcd10Code(code.code))
      .sort(RefData.compareIcd10Codes)
      .join('|') ?? ''
  )
}

export interface DDEIndicationAge {
  ptagemin?: number
  ptageminuom?: string
  ptagemax?: number
  ptagemaxuom?: string
}

export const toPositiveNumber = (
  value: number | string | null | undefined,
): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined
  const num = Number(value)
  return isNaN(num) || num <= 0 ? undefined : num
}

/**
 * Safely converts an age value (string or number) to a number.
 * Returns undefined for empty strings, null, undefined, or invalid values.
 */
export const parseAgeValue = (
  value: number | string | null | undefined,
): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return undefined
    const parsed = Number(trimmed)
    return isNaN(parsed) || parsed <= 0 ? undefined : parsed
  }

  return value > 0 ? value : undefined
}

export const buildIndicationAge = (
  indication: DdeIndicationInput,
  type: PolicyIndicationEditType,
): DDEIndicationAge => {
  if (type !== POLICY_INDICATION_EDIT_TYPE.Age) {
    return {
      ptagemin: undefined,
      ptageminuom: undefined,
      ptagemax: undefined,
      ptagemaxuom: undefined,
    }
  }
  return {
    ptagemin: parseAgeValue(indication.ageMin),
    ptageminuom: indication.ageMinUom || undefined,
    ptagemax: parseAgeValue(indication.ageMax),
    ptagemaxuom: indication.ageMaxUom || undefined,
  }
}

export interface DDEIndicationFrequency {
  mfot_indicator: 0 | 1
  mfot?: number
  freq_lookbacktype?: 'Rolling'
  freq_timeperiod?: number
  freq_timeperioduom?: TimePeriod
}

export const buildIndicationFrequency = (
  indication: DdeIndicationInput,
  type: PolicyIndicationEditType,
  bufferIntervals = true,
): DDEIndicationFrequency => {
  if (type !== POLICY_INDICATION_EDIT_TYPE.Frequency) {
    return {
      mfot_indicator: 0,
      mfot: undefined,
      freq_lookbacktype: undefined,
      freq_timeperiod: undefined,
      freq_timeperioduom: undefined,
    }
  }

  const adjustment = applyBufferDayAdjustment(
    indication.frequencyMaxFrequency,
    indication.frequencyTimePeriod,
    bufferIntervals,
  )

  return {
    mfot_indicator: 1,
    mfot: toPositiveNumber(indication.frequencyMaxAdminDays),
    freq_lookbacktype: 'Rolling',
    freq_timeperiod:
      adjustment.adjustedDays ??
      toPositiveNumber(indication.frequencyMaxFrequency),
    freq_timeperioduom: adjustment.adjustedDays
      ? 'day'
      : (indication.frequencyTimePeriod ?? undefined),
  }
}

export interface DDEIndicationMaxDailyUnits {
  dailymaxunit_indicator: 0 | 1
  dailymaximumunits?: number
}

export const buildIndicationMaxDailyUnits = (
  indication: DdeIndicationInput,
  type: PolicyIndicationEditType,
): DDEIndicationMaxDailyUnits => {
  if (type !== POLICY_INDICATION_EDIT_TYPE.MaxDailyUnits) {
    return {
      dailymaxunit_indicator: 0,
      dailymaximumunits: undefined,
    }
  }
  return {
    dailymaxunit_indicator: 1,
    dailymaximumunits: toPositiveNumber(indication.maxDailyHcpcsUnits),
  }
}

export interface DDEIndicationMaxUnitsOverTime {
  muot_indicator: 0 | 1
  muot?: number
  unit_lookbacktype?: 'Rolling'
  unit_timeperiod?: number
  unit_timeperioduom?: TimePeriod
}

export const buildIndicationMaxUnitsOverTime = (
  indication: DdeIndicationInput,
  type: PolicyIndicationEditType,
  bufferIntervals = true,
): DDEIndicationMaxUnitsOverTime => {
  if (type !== POLICY_INDICATION_EDIT_TYPE.MaxUnitsOverTime) {
    return {
      muot_indicator: 0,
      muot: undefined,
      unit_lookbacktype: undefined,
      unit_timeperiod: undefined,
      unit_timeperioduom: undefined,
    }
  }

  const adjustment = applyBufferDayAdjustment(
    indication.maxUnitsOverHcpcsMaxFrequency,
    indication.maxUnitsOverHcpcsTimePeriod,
    bufferIntervals,
  )

  return {
    muot_indicator: 1,
    muot: toPositiveNumber(indication.maxUnitsOverTimeMaxHCPCSUnits),
    unit_lookbacktype: 'Rolling',
    unit_timeperiod:
      adjustment.adjustedDays ??
      toPositiveNumber(indication.maxUnitsOverHcpcsMaxFrequency),
    unit_timeperioduom: adjustment.adjustedDays
      ? 'day'
      : (indication.maxUnitsOverHcpcsTimePeriod ?? undefined),
  }
}

const indicationTypeOrder = (type?: string): number =>
  type === 'ON_LABEL' ? 0 : 1

/**
 * Sorts indications for export: ON_LABEL first, then OFF_LABEL,
 * alphabetically by name within each group.
 * Indications with undefined type are treated as OFF_LABEL.
 */
export const sortIndicationsForExport = <T extends DdeIndicationInput>(
  indications: T[],
): T[] => {
  return [...indications].sort((a, b) => {
    const typeCompare =
      indicationTypeOrder(a.type) - indicationTypeOrder(b.type)
    if (typeCompare !== 0) return typeCompare

    return (a.name ?? '').localeCompare(b.name ?? '')
  })
}

export interface DDERow
  extends DDEDrugDetails,
    DDEIndicationDetails,
    Partial<DDEIndicationAge>,
    Partial<DDEIndicationFrequency>,
    Partial<DDEIndicationMaxDailyUnits>,
    Partial<DDEIndicationMaxUnitsOverTime> {
  policy_name: string
  policy_id?: string
  mfot_indicator: 0 | 1
  dailymaxunit_indicator: 0 | 1
  muot_indicator: 0 | 1
  edit_type: PolicyIndicationEditType
}
/** Narrow shape of the policy map consumed by the DDE export — just enough to fill in `policy_id`. */
export type DdePolicyMap = Partial<
  Record<PolicyIndicationEditType, { policyId?: string }>
>

export const buildDDERowsFromDrugIndications = (
  drug: DdeDrugInput,
  indications: DdeIndicationInput[],
  policies: DdePolicyMap,
  bufferIntervals = true,
) => {
  const rows: DDERow[] = []

  const drugDetails = buildDrugDetails(drug)

  const sortedIndications = sortIndicationsForExport(indications)
  for (const indication of sortedIndications) {
    const indicationTypes = parseIndicationTypes(indication)

    const indicationDetails = buildIndicationDetails(indication)
    for (const type of indicationTypes) {
      const policyName = formatDrugPolicyName(drug, type)
      const indicationAge = buildIndicationAge(indication, type)
      const indicationFrequency = buildIndicationFrequency(
        indication,
        type,
        bufferIntervals,
      )
      const indicationMaxDailyUnits = buildIndicationMaxDailyUnits(
        indication,
        type,
      )
      const indicationMaxUnitsOverTime = buildIndicationMaxUnitsOverTime(
        indication,
        type,
        bufferIntervals,
      )
      rows.push({
        ...drugDetails,
        ...indicationDetails,
        ...indicationAge,
        ...indicationFrequency,
        ...indicationMaxDailyUnits,
        ...indicationMaxUnitsOverTime,
        policy_name: policyName,
        policy_id: policies[type]?.policyId,
        edit_type: type,
      })
    }
  }
  return rows
}

export const ddeReferenceCols: Record<
  PolicyIndicationEditType,
  (keyof DDERow)[]
> = {
  Diagnosis: [
    'policy_name',
    'policy_id',
    'drugcode',
    'eff_start_date',
    'eff_end_date',
    'dxcodes',
  ],
  Age: [
    'policy_name',
    'policy_id',
    'drugcode',
    'eff_start_date',
    'eff_end_date',
    'dxcodes',
    'ptagemin',
    'ptageminuom',
    'ptagemax',
    'ptagemaxuom',
  ],
  Frequency: [
    'policy_name',
    'policy_id',
    'drugcode',
    'eff_start_date',
    'eff_end_date',
    'dxcodes',
    'mfot',
    'freq_lookbacktype',
    'freq_timeperiod',
    'freq_timeperioduom',
  ],
  MaxDailyUnits: [
    'policy_name',
    'policy_id',
    'drugcode',
    'eff_start_date',
    'eff_end_date',
    'dxcodes',
    'dailymaximumunits',
  ],
  MaxUnitsOverTime: [
    'policy_name',
    'policy_id',
    'drugcode',
    'eff_start_date',
    'eff_end_date',
    'dxcodes',
    'muot',
    'unit_lookbacktype',
    'unit_timeperiod',
    'unit_timeperioduom',
  ],
}
